import { api } from './api';

export type Category = {
  id: number;
  name: string;
  sortOrder: number;
  archived: boolean;
  static: boolean;
};

export type CategorySummary = Category & {
  limitCents: number;
  spentCents: number;
  remainingCents: number;
  overBudget: boolean;
};

export type MonthSummary = {
  year: number;
  month: number;
  seeded: boolean;
  categories: CategorySummary[];
  totals: { limitCents: number; spentCents: number; remainingCents: number };
};

export type Transaction = {
  id: number;
  categoryId: number;
  categoryName: string;
  amountCents: number;
  note: string | null;
  date: string;
  createdBy: { id: number; displayName: string };
};

export type HistoryMonth = {
  year: number;
  month: number;
  limitCents: number;
  spentCents: number;
  remainingCents: number;
  transactionCount: number;
  overBudget: boolean;
};

export const budget = {
  month: (year: number, month: number) =>
    api.get<MonthSummary>(`/api/budget/months/${year}/${month}`),

  history: () => api.get<{ months: HistoryMonth[] }>('/api/budget/history'),

  transactions: (year: number, month: number) =>
    api.get<{ transactions: Transaction[] }>(`/api/budget/months/${year}/${month}/transactions`),

  categories: (includeArchived = false) =>
    api.get<{ categories: Category[] }>(
      `/api/budget/categories${includeArchived ? '?archived=true' : ''}`,
    ),

  addCategory: (name: string, limitCents?: number, isStatic?: boolean) =>
    api.post<{ id: number }>('/api/budget/categories', {
      name,
      limitCents,
      static: isStatic,
    }),

  updateCategory: (
    id: number,
    patch: { name?: string; archived?: boolean; sortOrder?: number; static?: boolean },
  ) => api.patch(`/api/budget/categories/${id}`, patch),

  deleteCategory: (id: number) => api.del(`/api/budget/categories/${id}`),

  setLimits: (year: number, month: number, limits: { categoryId: number; limitCents: number }[]) =>
    api.put<MonthSummary>(`/api/budget/months/${year}/${month}/limits`, { limits }),

  addTransaction: (
    year: number,
    month: number,
    tx: { categoryId: number; amountCents: number; date: string; note?: string | null },
  ) => api.post<{ id: number }>(`/api/budget/months/${year}/${month}/transactions`, tx),

  updateTransaction: (
    id: number,
    patch: { categoryId?: number; amountCents?: number; date?: string; note?: string | null },
  ) => api.patch(`/api/budget/transactions/${id}`, patch),

  deleteTransaction: (id: number) => api.del(`/api/budget/transactions/${id}`),
};

/** Month arithmetic that wraps the year correctly. */
export function shiftMonth(year: number, month: number, delta: number): { year: number; month: number } {
  const zero = year * 12 + (month - 1) + delta;
  return { year: Math.floor(zero / 12), month: (zero % 12) + 1 };
}

export function periodIndex(year: number, month: number): number {
  return year * 12 + month;
}

/** Current calendar month in the browser's local timezone. */
export function localPeriod(now = new Date()): { year: number; month: number } {
  return { year: now.getFullYear(), month: now.getMonth() + 1 };
}

/** Current month and the next one — as far ahead as Budget will go. */
export function maxPlanningPeriod(now = new Date()): { year: number; month: number } {
  const { year, month } = localPeriod(now);
  return shiftMonth(year, month, 1);
}

export function isBeyondPlanningHorizon(
  year: number,
  month: number,
  now = new Date(),
): boolean {
  const max = maxPlanningPeriod(now);
  return periodIndex(year, month) > periodIndex(max.year, max.month);
}

/** YYYY-MM-DD of the last day of a month, for the expense date picker. */
export function lastDayOfMonth(year: number, month: number): string {
  const d = new Date(Date.UTC(year, month, 0));
  return d.toISOString().slice(0, 10);
}

/** Budget page for a specific month. History and the month picker both use this. */
export function budgetMonthPath(year: number, month: number): string {
  return `/budget?y=${year}&m=${month}`;
}
